/**
 * @author        david.loehr@zumper.com (David Loehr)
 * @copyright     Copyright (c) 2023, Zumper
 */

import { filterActions } from '@zumper/redux-ignore'
import type { Reducer } from 'redux'
import type { Action, NarrowedAction } from 'redux-actions'

/**
 * Allows predefinedState to be merged with the default state defined by the
 * reducer.
 *
 * This is a built-in feature of redux that the redux-ignore library breaks (
 * see https://github.com/omnidan/redux-ignore/issues/12)
 */
export function safeFilterActions<
  State,
  Actions extends Action<string, unknown>,
  FilteredActionTypes extends string,
>(
  reducer: Reducer<State, NarrowedAction<Actions, FilteredActionTypes>>,
  actions: readonly FilteredActionTypes[]
): Reducer<State, Actions>
export function safeFilterActions<
  State,
  Actions extends Action<string, unknown>,
>(
  reducer: Reducer<State, Actions>,
  actions: ReadonlyArray<Actions['type']> | ((action: Actions) => boolean)
): Reducer<State, Actions>
export function safeFilterActions<
  State,
  Actions extends Action<string, unknown>,
>(
  reducer: Reducer<State, Actions>,
  actions: ReadonlyArray<Actions['type']> | ((action: Actions) => boolean)
): Reducer<State, Actions> {
  let predicate: (action: Actions) => boolean
  if (typeof actions === 'function') {
    predicate = (action: Actions) => {
      return (
        actions(action) ||
        // NOTE: redux considers the @@redux/ actions to be private and doesn't
        // want our code to mention them
        (typeof action.type === 'string' &&
          action.type.substring(0, 8) === '@@redux/')
      )
    }
  } else {
    const ACTION_TYPES = new Set(actions)
    predicate = (action: Actions) => {
      return (
        ACTION_TYPES.has(action.type) ||
        // NOTE: redux considers the @@redux/ actions to be private and doesn't
        // want our code to mention them
        (typeof action.type === 'string' &&
          action.type.substring(0, 8) === '@@redux/')
      )
    }
  }
  return filterActions(reducer, predicate)
}
